import useSSE from '../useSSE';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

function escapeXml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

const STATUS_COLORS = {
  not_started:'#8A93A6', planning:'#2563EB', in_progress:'#5B5BD6',
  escalated:'#DC2626', at_risk:'#D97706', on_hold:'#C4B5FD',
  completed:'#16A34A', cancelled:'#6B7280'
};
const STATUS_LABELS = {
  not_started:'Not Started', planning:'Planning', in_progress:'In Progress',
  escalated:'Escalated', at_risk:'At Risk', on_hold:'On Hold',
  completed:'Completed', cancelled:'Cancelled'
};

function daysBetween(a,b) { return Math.round((new Date(b)-new Date(a))/86400000); }
function addDays(d,n) { const dt=new Date(d); dt.setDate(dt.getDate()+n); return dt.toISOString().slice(0,10); }
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d+'T00:00:00').toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'});
}

// Milestone diamond icon SVG
function MilestoneIcon({color='#D97706',size=16}) {
  // ClickUp-style: solid filled diamond (rotated square) — professional milestone marker
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M8 0L16 8L8 16L0 8Z" fill={color}/>
      <path d="M8 3L13 8L8 13L3 8Z" fill="white" opacity="0.25"/>
    </svg>
  );
}
// Meeting clock icon SVG
function MeetingIcon({color='#0891B2',size=14}) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="3" width="11" height="9.5" rx="1.5" fill="none" stroke={color} strokeWidth="1.2"/>
      <rect x="1.5" y="3" width="11" height="3.2" rx="1.5" fill={color} opacity="0.85"/>
      <line x1="4.5" y1="1.5" x2="4.5" y2="4.5" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="9.5" y1="1.5" x2="9.5" y2="4.5" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <circle cx="4.5" cy="9" r="0.85" fill={color} opacity="0.75"/>
      <circle cx="7" cy="9" r="0.85" fill={color} opacity="0.75"/>
      <circle cx="9.5" cy="9" r="0.85" fill={color} opacity="0.75"/>
    </svg>
  );
}

export default function GanttPage() {
  const { spaceId } = useParams();
  // Back to dashboard


  const navigate = useNavigate();

  // Real-time SSE
  useSSE(spaceId, {
    task_updated: () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
  });

  // Silent reload when phase access changes (permission granted/revoked)
  React.useEffect(() => {
    function onAccessChanged() {
      silentRef.current = true;
      load().finally(() => { silentRef.current = false; });
    }
    window.addEventListener('trajectory:access_changed', onAccessChanged);
    return () => window.removeEventListener('trajectory:access_changed', onAccessChanged);
  }, [spaceId]);
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [showBar, setShowBar] = useState('both');
  const [zoom, setZoom] = useState('month');
  const [ganttStart, setGanttStart] = useState('');
  const ganttStartRef = React.useRef('');
  const [ganttEnd, setGanttEnd] = useState('');
  const [spaceName, setSpaceName] = useState('');
  const [printMenu, setPrintMenu] = useState(false);
  const [criticalSet, setCriticalSet] = useState(new Set());
  const [dateIssues, setDateIssues] = useState([]);
  const [showCritical, setShowCritical] = useState(false);
  const [baselines, setBaselines] = useState({});
  const [baselineAt, setBaselineAt] = useState('');
  const [showBaseline, setShowBaseline] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [gMsg, setGMsg] = useState('');
  const [tooltip, setTooltip] = useState(null);
  const [phaseFilter, setPhaseFilter] = useState('all'); // 'all' or phaseId
  const [phases, setPhases] = useState([]); // list of {id, name, color}
  const [exportMsg, setExportMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const silentRef = React.useRef(false); // true during silent poll refresh

  const bodyRef = useRef();
  const labelRef = useRef();
  const ganttRef = useRef();

  useEffect(()=>{ load(); },[spaceId]);
  // ── Silent background refresh ─────────────────────────────────────────────
  useEffect(()=>{
    let lastTs=null, active=true, fails=0;
    const poll=async()=>{
      if(!active||!spaceId) return;
      try {
        const r=await api.get(`/spaces/${spaceId}/last-updated`).catch(()=>null);
        if(!r){fails++;if(fails>4)active=false;return;}
        fails=0;
        if(lastTs===null){lastTs=r.last_updated;return;}
        if(r.last_updated!==lastTs){
          lastTs=r.last_updated;
          silentRef.current=true;
          load().finally(()=>{silentRef.current=false;});
        }
      } catch(_){fails++;}
    };
    const iv=setInterval(poll,15000);
    return()=>{active=false;clearInterval(iv);};
  },[spaceId]);



  // Scroll to today whenever rows load OR zoom changes
  useEffect(()=>{
    if (rows.length > 0 && bodyRef.current && ganttStart) {
      const todayStr = new Date().toISOString().slice(0,10);
      const days = Math.floor((new Date(todayStr) - new Date(ganttStart)) / 86400000);
      const dw = zoom==='week'?32:zoom==='month'?18:zoom==='quarter'?8:3;
      bodyRef.current.scrollLeft = Math.max(0, days * dw - 300);
    }
  },[rows.length, ganttStart, zoom]);
  async function load() {
    if(!silentRef.current) setLoading(true);
    try {
      // Single API call — returns ALL phases/lists/tasks at once, no sequential requests
      const [space, ganttData] = await Promise.all([
        api.get(`/spaces/${spaceId}`),
        api.get(`/spaces/${spaceId}/gantt`),
      ]);

      setSpaceName(space.name||'Project');
      setPhases((ganttData||[]).map(f=>({id:f.id, name:f.name, color:f.color||'#D97706'})));

      const allRows = [], allDates = [];

      for (const folder of (ganttData||[])) {
        allRows.push({ type:'phase', id:folder.id, name:folder.name, color:folder.color||'#D97706', hasChildren:(folder.lists||[]).length>0 });

        for (const list of (folder.lists||[])) {
          const top = list.tasks||[];
          const dates = top.flatMap(t=>[t.start_date,t.due_date,t.actual_start_date,t.actual_end_date]).filter(Boolean);
          allDates.push(...dates);
          const ls=[...dates].sort()[0]||null, le=[...dates].sort().reverse()[0]||null;
          allRows.push({
            type:'milestone', id:list.id, name:list.name, color:list.color||'#5B5BD6',
            start:ls, end:le, phaseId:folder.id, hasChildren:top.length>0,
          });

          // Collect all dates from a node tree (any depth)
          function collectAllDates(node) {
            const d = [node.start_date,node.due_date,node.actual_start_date,node.actual_end_date].filter(Boolean);
            (node.subtasks||[]).forEach(ch=>d.push(...collectAllDates(ch)));
            return d;
          }

          top.forEach(task=>{
            allRows.push({
              type:'task', id:task.id, name:task.name, milestoneId:list.id, phaseId:folder.id,
              status:task.status, task_type:task.task_type||'task',
              start:task.start_date, end:task.due_date,
              actual_start:task.actual_start_date, actual_end:task.actual_end_date,
              progress:task.calculatedProgress??task.progress??0,
              hasChildren:(task.subtasks||[]).length>0,
              assignees:task.assignees||[], time_estimate:task.time_estimate||0,
            });
            // Sub-subtasks (level 2+) are hidden from Gantt.
            // Their dates are folded into the subtask span automatically.
            (task.subtasks||[]).forEach(st=>{
              const allD = collectAllDates(st).sort();
              const s = st.start_date || allD[0] || null;
              const e = st.due_date   || allD[allD.length-1] || null;
              allRows.push({
                type:'subtask', id:st.id, parentId:task.id, milestoneId:list.id, phaseId:folder.id,
                name:st.name, status:st.status, task_type:st.task_type||'task',
                start:s, end:e,
                actual_start:st.actual_start_date, actual_end:st.actual_end_date,
                progress:st.calculatedProgress??st.progress??0,
                // show expand toggle in label panel but don't render sub-subtask rows
                hasChildren:false, assignees:st.assignees||[],
              });
            });
          });
        }
      }

      setRows(allRows);
      // Critical path + baseline (best-effort; don't block the chart)
      api.get(`/spaces/${spaceId}/critical-path`).then(r=>{ setCriticalSet(new Set(r.critical||[])); setDateIssues(r.dateIssues||[]); }).catch(()=>{});
      api.get(`/spaces/${spaceId}/baseline`).then(r=>{ setBaselines(r.baselines||{}); setBaselineAt(r.capturedAt||''); }).catch(()=>{});
      if (allDates.length>0) {
        const sorted=[...allDates].sort();
        const start = addDays(sorted[0],-14);
        const dataEnd = addDays(sorted[sorted.length-1],21);
        // Always show at least 3 years from project start for long projects
        const threeYears = new Date(sorted[0]);
        threeYears.setFullYear(threeYears.getFullYear()+3);
        const minEnd = threeYears.toISOString().slice(0,10);
        setGanttStart(start); ganttStartRef.current = start;
        setGanttEnd(dataEnd > minEnd ? dataEnd : minEnd);
      } else {
        const t=new Date().toISOString().slice(0,10);
        const end = new Date(t); end.setFullYear(end.getFullYear()+3);
        const gs=addDays(t,-30); setGanttStart(gs); ganttStartRef.current=gs; setGanttEnd(end.toISOString().slice(0,10));
      }
      const exp={};
      allRows.filter(r=>r.type==='phase'||r.type==='milestone').forEach(r=>{exp[r.id]=true;});
      setExpanded(exp);
    } catch(e){ console.error(e); }
    setLoading(false);
    // (scroll to today handled by useEffect)
  }


  if (!ganttStart || loading) return (
    <div className="page-enter" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)',gap:'10px'}}>
      
      <div style={{width:16,height:16,border:'2px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
      Loading Gantt chart…
    </div>
  );

  const DAY_W = zoom==='week'?32:zoom==='month'?18:zoom==='quarter'?8:3; // year=3px/day
  const totalDays = daysBetween(ganttStart,ganttEnd)+1;
  const totalW = totalDays*DAY_W;
  const ROW_H = 44;
  const LABEL_W = 320;
  const today = new Date().toISOString().slice(0,10);
  const todayOff = daysBetween(ganttStart,today);

  // Month headers
  const months=[];
  let cur=new Date(ganttStart), endDt=new Date(ganttEnd);
  while(cur<=endDt) {
    const y=cur.getFullYear(),m=cur.getMonth();
    let days=0;
    const label = zoom==='year' ? cur.toLocaleDateString('en-US',{month:'short'}) : cur.toLocaleDateString('en-US',{month:'short',year:'2-digit'});
    while(cur<=endDt&&cur.getFullYear()===y&&cur.getMonth()===m){days++;cur.setDate(cur.getDate()+1);}
    months.push({label,days,year:y});
  }

  // Visible rows — respect phase filter
  const visible=rows.filter(r=>{
    // Phase filter
    if (phaseFilter !== 'all') {
      if (r.type==='phase') return r.id === phaseFilter;
      if (r.phaseId && r.phaseId !== phaseFilter) return false;
    }
    if(r.type==='phase') return true;
    if(r.type==='milestone') return !!expanded[r.phaseId];
    if(r.type==='task') return !!expanded[r.phaseId]&&!!expanded[r.milestoneId];
    if(r.type==='subtask') return !!expanded[r.phaseId]&&!!expanded[r.milestoneId]&&!!expanded[r.parentId];
    return false;
  });

  function barProps(row) {
    // For milestone task_type: just due date marker
    if (row.task_type==='milestone' && row.end) {
      const left=Math.max(0,daysBetween(ganttStart,row.end))*DAY_W;
      return {left,width:DAY_W,isDiamond:true};
    }
    // For meeting: use start date as the date
    const s=row.start, e=row.end||row.start;
    if(!s) return null;
    const left=Math.max(0,daysBetween(ganttStart,s))*DAY_W;
    const width=Math.max(DAY_W,(daysBetween(s,e)||1)*DAY_W+DAY_W);
    return {left,width,isDiamond:false};
  }

  function actualBarProps(row) {
    // Only show actual bar when actual_start_date is explicitly set
    // A new task with no actual dates should show NO actual bar
    const s = row.actual_start;  // strict — must be a real actual date
    if (!s) return null;
    // End: use actual_end if set, otherwise today (task in progress)
    const e = row.actual_end || today;
    const left = Math.max(0, daysBetween(ganttStart, s)) * DAY_W;
    const width = Math.max(DAY_W, (daysBetween(s, e) || 1) * DAY_W + DAY_W); // match planned bar formula
    return { left, width };
  }

  function rowColor(row) {
    if(row.type==='phase') return row.color||'#D97706';
    if(row.type==='milestone') return row.color||'#0891B2';
    return STATUS_COLORS[row.status]||'#8A93A6';
  }

  function rowBg(row) {
    if(row.type==='phase') return 'rgba(217,119,6,0.03)';
    if(row.type==='subtask') return 'rgba(91,91,214,0.02)';
    return 'transparent';
  }

  function syncScroll(e){
    if(labelRef.current) labelRef.current.scrollTop=e.target.scrollTop;
  }

  const scrollToToday=()=>{
    if(bodyRef.current) bodyRef.current.scrollLeft=Math.max(0,todayOff*DAY_W-200);
  };

  function printGanttFitted(paper, orientation, fitMode) {
    const el = ganttRef.current;
    if (!el) return;
    setPrintMenu(false);
    printElementFitted({
      el,
      title: `${spaceName || 'Project'} — Gantt Chart`,
      subtitle: 'Space Communication Technologies',
      paper, orientation, fitMode,
    });
  }

  async function captureBaseline() {
    setCapturing(true); setGMsg('');
    try {
      const r = await api.post(`/spaces/${spaceId}/baseline`, {});
      setGMsg(`Baseline captured (${r.captured} tasks).`);
      const b = await api.get(`/spaces/${spaceId}/baseline`).catch(()=>null);
      if (b) { setBaselines(b.baselines||{}); setBaselineAt(b.capturedAt||''); }
      setShowBaseline(true);
    } catch(e) { setGMsg(e?.error||'Could not capture baseline.'); }
    finally { setCapturing(false); setTimeout(()=>setGMsg(''),5000); }
  }

  function exportGanttSvg() {
    const el = ganttRef.current;
    if (!el) return;

    // Clone the chart so we can expand it to full size without affecting the page.
    const clone = el.cloneNode(true);
    clone.style.overflow = 'visible';
    clone.style.width = el.scrollWidth + 'px';
    clone.style.height = el.scrollHeight + 'px';

    let inner = clone.outerHTML;
    // Inline SVG icons (milestone diamonds, meeting clocks) lose their namespace
    // when serialised into a standalone SVG <foreignObject>, so they vanish.
    // Re-declare the SVG namespace on every nested <svg> so they render.
    inner = inner.replace(/<svg(?![^>]*xmlns)/g, '<svg xmlns="http://www.w3.org/2000/svg"');

    const chartW = el.scrollWidth;
    const chartH = el.scrollHeight;
    const padX = 40, padTop = 90, padBottom = 30;
    const w = chartW + padX * 2;
    const h = chartH + padTop + padBottom;
    const title = `${spaceName || 'Project'} — Gantt Chart`;
    const dateStr = new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' });

    // Professional header + legend, drawn as native SVG so it always renders.
    const header = `
      <rect x="0" y="0" width="${w}" height="64" fill="#1E2761"/>
      <text x="${padX}" y="30" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="#FFFFFF">${escapeXml(title)}</text>
      <text x="${padX}" y="50" font-family="Arial, sans-serif" font-size="11" fill="#9DB0D8">Exported ${dateStr}</text>
      <g font-family="Arial, sans-serif" font-size="11" fill="#475569">
        <rect x="${padX}" y="72" width="22" height="9" rx="2" fill="#5B5BD6"/>
        <text x="${padX+28}" y="80">Planned bar</text>
        <rect x="${padX+120}" y="74" width="22" height="6" rx="2" fill="#F97316"/>
        <text x="${padX+148}" y="80">Actual progress</text>
        <path d="M ${padX+265} 70 l 8 8 l -8 8 l -8 -8 z" fill="#D97706"/>
        <text x="${padX+280}" y="80">Milestone</text>
        <circle cx="${padX+365}" cy="77" r="7" fill="#0891B2"/>
        <path d="M ${padX+365} 73 v 4 l 2.5 2" stroke="#fff" stroke-width="1.3" stroke-linecap="round" fill="none"/>
        <text x="${padX+378}" y="80">Meeting</text>
      </g>`;

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <style>
    * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; box-sizing: border-box; }
  </style>
  <rect width="${w}" height="${h}" fill="#F8F9FC"/>
  ${header}
  <foreignObject x="${padX}" y="${padTop}" width="${chartW}" height="${chartH}">
    <div xmlns="http://www.w3.org/1999/xhtml" style="background:#F8F9FC">
      ${inner}
    </div>
  </foreignObject>
</svg>`;

    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Gantt_${(spaceName||'Project').replace(/[^a-z0-9]/gi,'_')}_${new Date().toISOString().slice(0,10)}.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setExportMsg('Saved! Open the .svg in your browser (zoomable) or print to PDF.');
    setTimeout(()=>setExportMsg(''),6000);
  }


  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:'var(--bg)'}}>

      {/* Toolbar */}
      <div style={{padding:'10px 20px',background:'var(--surface)',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'12px',flexShrink:0}}>
        <div>
          <h2 style={{fontSize:'16px',fontWeight:'700',color:'var(--text)',margin:0}}>Gantt Chart</h2>
          <p style={{fontSize:'11px',color:'var(--muted)',margin:0}}>{spaceName}</p>
        </div>
        <div style={{flex:1}}/>

        {/* Phase Filter */}
        {phases.length > 1 && (
          <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
            <span style={{fontSize:'11px',color:'var(--muted)',fontWeight:'600',flexShrink:0}}>Phase:</span>
            <div style={{display:'flex',gap:'4px',flexWrap:'wrap',maxWidth:'340px'}}>
              <button onClick={()=>setPhaseFilter('all')}
                style={{padding:'3px 10px',borderRadius:'99px',border:`1.5px solid ${phaseFilter==='all'?'var(--brand)':'var(--border)'}`,background:phaseFilter==='all'?'var(--brand-dim)':'transparent',color:phaseFilter==='all'?'var(--brand)':'var(--muted)',fontSize:'11px',fontWeight:'600',cursor:'pointer',transition:'all .15s'}}>
                All
              </button>
              {phases.map(ph=>(
                <button key={ph.id} onClick={()=>setPhaseFilter(ph.id)}
                  style={{padding:'3px 10px',borderRadius:'99px',border:`1.5px solid ${phaseFilter===ph.id?ph.color:'var(--border)'}`,background:phaseFilter===ph.id?ph.color+'18':'transparent',color:phaseFilter===ph.id?ph.color:'var(--muted)',fontSize:'11px',fontWeight:'600',cursor:'pointer',transition:'all .15s',maxWidth:'100px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {ph.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bar mode */}
        <div style={{order:1,display:'flex',background:'var(--surface2)',borderRadius:'var(--radius)',padding:'2px',border:'1px solid var(--border)',gap:'2px'}}>
          {[['planned','Planned'],['actual','Actual'],['both','Both']].map(([v,l])=>(
            <button key={v} onClick={()=>setShowBar(v)}
              style={{padding:'4px 12px',borderRadius:'6px',border:'none',background:showBar===v?'#fff':'none',color:showBar===v?'var(--text)':'var(--muted)',fontSize:'12px',fontWeight:'600',cursor:'pointer',boxShadow:showBar===v?'var(--shadow-sm)':'none',transition:'all .15s'}}>
              {l}
            </button>
          ))}
        </div>

        {/* ── Analysis panel: critical path + baseline, grouped in a bordered box ── */}
        <div style={{order:4,display:'flex',alignItems:'center',gap:'8px',padding:'4px 10px',border:'1px solid var(--border)',borderRadius:'10px',background:'var(--surface)'}}>
          <span style={{fontSize:'9px',fontWeight:'800',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.6px'}}>Analysis</span>
          <button onClick={()=>setShowCritical(s=>!s)} title="Highlight the critical path — the chain of tasks with zero schedule slack that drives the project finish date (completed tasks excluded)"
            style={{padding:'4px 10px',borderRadius:'7px',border:`1.5px solid ${showCritical?'#DC2626':'var(--border)'}`,background:showCritical?'rgba(220,38,38,0.08)':'transparent',color:showCritical?'#DC2626':'var(--muted)',fontSize:'11.5px',fontWeight:'700',cursor:'pointer'}}>
            ▲ Critical Path
          </button>
          <button onClick={()=>setShowBaseline(s=>!s)} disabled={!baselineAt} title={baselineAt?`Baseline captured ${new Date(baselineAt).toLocaleString('en-GB')}`:'No baseline captured yet'}
            style={{padding:'4px 10px',borderRadius:'7px',border:`1.5px solid ${showBaseline&&baselineAt?'#0891B2':'var(--border)'}`,background:showBaseline&&baselineAt?'rgba(8,145,178,0.08)':'transparent',color:!baselineAt?'var(--muted2)':showBaseline?'#0891B2':'var(--muted)',fontSize:'11.5px',fontWeight:'700',cursor:baselineAt?'pointer':'not-allowed',opacity:baselineAt?1:0.6}}>
            ▭ Baseline
          </button>
          <button onClick={captureBaseline} disabled={capturing} title="Save current planned dates as the baseline (replaces any previous one)"
            style={{padding:'4px 10px',borderRadius:'7px',border:'1.5px solid var(--border)',background:'transparent',color:'var(--muted)',fontSize:'11.5px',fontWeight:'600',cursor:'pointer'}}>
            {capturing?'…':(baselineAt?'🔄 Re-capture':'📸 Capture baseline')}
          </button>
          {baselineAt
            ? <span style={{fontSize:'10px',color:'var(--muted)'}}>captured {new Date(baselineAt).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})} {new Date(baselineAt).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}</span>
            : <span style={{fontSize:'10px',color:'var(--muted2)'}}>no baseline yet</span>}
          {gMsg && <span style={{fontSize:'10.5px',fontWeight:'600',color:gMsg.toLowerCase().includes('not')||gMsg.toLowerCase().includes('could')?'#DC2626':'#16A34A'}}>{gMsg}</span>}
          {showCritical && dateIssues.length>0 && (
            <span title={dateIssues.map(d=>`${d.name}: end (${d.due}) is before start (${d.start})`).join('\n')}
              style={{fontSize:'10.5px',fontWeight:'700',color:'#B45309',background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:'6px',padding:'2px 8px',cursor:'help'}}>
              ⚠ {dateIssues.length} task{dateIssues.length>1?'s have':' has'} reversed dates — affects accuracy
            </span>
          )}
        </div>

        {/* Zoom */}
        <div style={{order:2,display:'flex',background:'var(--surface2)',borderRadius:'var(--radius)',padding:'2px',border:'1px solid var(--border)',gap:'2px'}}>
          {[['week','Week'],['month','Month'],['quarter','Qtr'],['year','Year']].map(([v,l])=>(
            <button key={v} onClick={()=>setZoom(v)}
              style={{padding:'4px 10px',borderRadius:'6px',border:'none',background:zoom===v?'#fff':'none',color:zoom===v?'var(--text)':'var(--muted)',fontSize:'12px',fontWeight:'600',cursor:'pointer',boxShadow:zoom===v?'var(--shadow-sm)':'none',transition:'all .15s'}}>
              {l}
            </button>
          ))}
        </div>

        <button className="btn btn-sm" style={{order:3}} onClick={scrollToToday}>📍 Today</button>

        {/* Export / Report dropdown */}
        <div style={{order:5,position:'relative',display:'inline-block'}}>
          <button className="btn btn-sm" onClick={()=>setPrintMenu(m=>!m)} title="Export and print options">📤 Export / Print ▾</button>
          {printMenu && (
            <>
              <div onClick={()=>setPrintMenu(false)} style={{position:'fixed',inset:0,zIndex:998}}/>
              <div style={{position:'absolute',top:'110%',right:0,zIndex:999,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'10px',boxShadow:'0 8px 24px rgba(0,0,0,0.15)',padding:'6px',minWidth:'250px'}}>
                <div style={{fontSize:'10px',fontWeight:'700',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.5px',padding:'6px 10px 4px'}}>Export</div>
                <button onClick={()=>{setPrintMenu(false);exportGanttSvg();}}
                  style={{display:'block',width:'100%',textAlign:'left',padding:'8px 10px',background:'none',border:'none',cursor:'pointer',fontSize:'12.5px',color:'var(--text)',borderRadius:'6px'}}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                  onMouseLeave={e=>e.currentTarget.style.background='none'}>
                  🖼️ Interactive SVG <span style={{color:'var(--muted)',fontSize:'10.5px'}}>(zoomable, opens in browser)</span>
                </button>
                <div style={{height:'1px',background:'var(--border)',margin:'4px 0'}}/>
                <div style={{fontSize:'10px',fontWeight:'700',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.5px',padding:'6px 10px 4px'}}>Print / PDF — fit to one page</div>
                {[['A4','landscape','A4 Landscape'],['A4','portrait','A4 Portrait'],['A3','landscape','A3 Landscape'],['A3','portrait','A3 Portrait']].map(([p,o,label])=>(
                  <button key={p+o} onClick={()=>printGanttFitted(p,o,'page')}
                    style={{display:'block',width:'100%',textAlign:'left',padding:'8px 10px',background:'none',border:'none',cursor:'pointer',fontSize:'12.5px',color:'var(--text)',borderRadius:'6px'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                    onMouseLeave={e=>e.currentTarget.style.background='none'}>
                    📄 {label}
                  </button>
                ))}
                <div style={{height:'1px',background:'var(--border)',margin:'4px 0'}}/>
                <button onClick={()=>printGanttFitted('A3','landscape','width')}
                  style={{display:'block',width:'100%',textAlign:'left',padding:'8px 10px',background:'none',border:'none',cursor:'pointer',fontSize:'12.5px',color:'var(--text)',borderRadius:'6px'}}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                  onMouseLeave={e=>e.currentTarget.style.background='none'}>
                  📐 Fit width (A3, flows down)
                </button>
              </div>
            </>
          )}
        </div>
        {exportMsg&&<span style={{order:6,fontSize:'11px',color:'#16A34A',fontWeight:'600',marginLeft:'4px'}}>{exportMsg}</span>}
      </div>

      {/* Legend */}
      <div style={{padding:'5px 20px',background:'var(--surface)',borderBottom:'1px solid var(--border)',display:'flex',gap:'16px',alignItems:'center',flexShrink:0,flexWrap:'wrap'}}>
        <span style={{fontSize:'10px',fontWeight:'700',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.6px'}}>Legend:</span>
        <LegendItem color="#5B5BD6" label="Planned bar" height={10}/>
        <LegendItem color="#F97316" label="Actual progress (orange)" height={6}/>
        <div style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'10px',color:'var(--muted)'}}>
          <MilestoneIcon size={12}/> Milestone task
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'10px',color:'var(--muted)'}}>
          <MeetingIcon size={12}/> Meeting task
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'10px',color:'var(--muted)'}}>
          <div style={{width:2,height:14,background:'#DC2626',borderRadius:'1px'}}/> Today
        </div>
        <div style={{flex:1}}/>
        <span style={{fontSize:'10px',color:'var(--muted)'}}>Hover over bars for details</span>
      </div>

      {/* Gantt body */}
      <div ref={ganttRef} style={{flex:1,display:'flex',overflow:'hidden',position:'relative'}}>

        {/* Label panel */}
        <div style={{width:LABEL_W,flexShrink:0,borderRight:'2px solid var(--border)',display:'flex',flexDirection:'column',background:'var(--surface)'}}>
          <div style={{height:ROW_H,borderBottom:'2px solid var(--border)',background:'var(--surface2)',display:'flex',alignItems:'center',padding:'0 12px',flexShrink:0}}>
            <span style={{fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted)'}}>Project Structure</span>
          </div>
          <div ref={labelRef} style={{flex:1,overflowY:'hidden'}}>
            {visible.map(row=>{
              const c=rowColor(row);
              const indent=row.type==='phase'?4:row.type==='milestone'?18:row.type==='task'?32:46;
              return (
                <div key={row.id+row.type}
                  style={{height:ROW_H,display:'flex',alignItems:'center',borderBottom:'1px solid var(--border)',background:rowBg(row),paddingLeft:indent,gap:'6px',transition:'background .1s'}}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                  onMouseLeave={e=>e.currentTarget.style.background=rowBg(row)}>
                  {row.hasChildren
                    ? <button onClick={()=>setExpanded(p=>({...p,[row.id]:!p[row.id]}))}
                        style={{background:'none',border:'none',cursor:'pointer',color:'var(--muted)',padding:'2px 3px',fontSize:'9px',flexShrink:0,transition:'transform .2s',transform:expanded[row.id]?'rotate(90deg)':'rotate(0deg)'}}>▶</button>
                    : <span style={{width:16,flexShrink:0}}/>}
                  {/* Type icon */}
                  {row.task_type==='milestone'
                    ? <MilestoneIcon color={c} size={12}/>
                    : row.task_type==='meeting'
                      ? <MeetingIcon color={c} size={12}/>
                      : <div style={{width:row.type==='phase'?10:7,height:row.type==='phase'?10:7,borderRadius:row.type==='phase'?'2px':'50%',background:c,flexShrink:0}}/>}
                  <span style={{fontSize:row.type==='phase'?'13px':'12px',fontWeight:row.type==='phase'?'700':row.type==='milestone'?'600':'500',color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,lineHeight:1.3}} title={row.name}>{row.name}</span>
                  {(row.type==='task'||row.type==='subtask')&&(
                    <span style={{fontSize:'10px',fontWeight:'700',color:row.progress>=100?'#16A34A':row.progress>=50?'#5B5BD6':'var(--muted)',paddingRight:'8px',flexShrink:0}}>{Math.round(row.progress)}%</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline */}
        <div ref={bodyRef} style={{flex:1,overflowX:'auto',overflowY:'auto'}} onScroll={syncScroll}>
          <div style={{width:totalW,minHeight:'100%',position:'relative'}}>

            {/* Year + Month headers */}
            <div style={{position:'sticky',top:0,zIndex:6}}>
              {/* Year row */}
              {zoom==='year' && <YearHeaderRow months={months} dayW={DAY_W}/>}
              {/* Month row */}
              <div style={{display:'flex',height:ROW_H-(zoom==='year'?22:0),borderBottom:'2px solid var(--border)',background:'var(--surface2)'}}>
                {months.map((m,i)=>(
                  <div key={i} style={{width:m.days*DAY_W,flexShrink:0,borderRight:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'700',color:'var(--muted2)',overflow:'hidden'}}>
                    {m.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Today line */}
            {todayOff>=0&&todayOff<=totalDays&&(
              <div style={{position:'absolute',left:todayOff*DAY_W+DAY_W/2,top:0,bottom:0,width:2,background:'#DC2626',opacity:.55,zIndex:5,pointerEvents:'none'}}/>
            )}

            {/* Rows */}
            {visible.map((row,i)=>{
              const c=rowColor(row);
              const isOverdue=row.end&&row.end<today&&row.status!=='completed';
              const barC=isOverdue?'#DC2626':c;
              const isPhase=row.type==='phase', isMS=row.type==='milestone';
              const planned=(showBar==='planned'||showBar==='both')?barProps(row):null;
              const actual=(showBar==='actual'||showBar==='both')?actualBarProps(row):null;
              const isCritical = showCritical && criticalSet.has(row.id);
              const criticalDone = isCritical && row.status==='completed';
              const criticalLive = isCritical && row.status!=='completed';
              const baseline = (showBaseline && baselines[row.id]) ? baselines[row.id] : null;
              const baseBar = baseline ? barProps({ ...row, start: baseline.start_date, end: baseline.due_date }) : null;
              const isMilestoneTask=row.task_type==='milestone';
              const isMeetingTask=row.task_type==='meeting';

              return (
                <div key={row.id+row.type} style={{height:ROW_H,position:'relative',borderBottom:'1px solid var(--border)',background:rowBg(row)}}>

                  {/* Week grid lines */}
                  {zoom==='week'&&Array.from({length:totalDays}).map((_,di)=>(
                    <div key={di} style={{position:'absolute',left:di*DAY_W,top:0,bottom:0,borderLeft:di%7===0?'1px solid var(--border)':'none',pointerEvents:'none'}}/>
                  ))}

                  {/* MILESTONE — planned diamond + actual diamond */}
                  {isMilestoneTask&&(()=>{
                    const plannedLeft = row.end ? Math.max(0,daysBetween(ganttStart,row.end))*DAY_W : null;
                    const actualLeft  = row.actual_end ? Math.max(0,daysBetween(ganttStart,row.actual_end))*DAY_W : null;
                    const PLANNED_COLOR = barC;        // task status color (blue/amber/green)
                    const ACTUAL_COLOR  = '#F97316';   // orange — distinct from planned
                    return (<>
                      {/* Planned date diamond (status color) */}
                      {plannedLeft!==null && (showBar==='planned'||showBar==='both') && (
                        <div style={{position:'absolute',left:plannedLeft-8,top:'26px',zIndex:4,cursor:'pointer'}}
                          onMouseEnter={e=>{const r=e.currentTarget.getBoundingClientRect();setTooltip({x:r.left+18,y:r.top-8,row});}}
                          onMouseLeave={()=>setTooltip(null)}>
                          <MilestoneIcon color={PLANNED_COLOR} size={22}/>
                          <div style={{position:'absolute',top:'100%',left:'50%',transform:'translateX(-50%)',fontSize:'8px',color:PLANNED_COLOR,fontWeight:'700',whiteSpace:'nowrap',marginTop:'1px'}}>P</div>
                        </div>
                      )}
                      {/* Actual end date diamond (orange) */}
                      {actualLeft!==null && (showBar==='actual'||showBar==='both') && actualLeft!==plannedLeft && (
                        <div style={{position:'absolute',left:actualLeft-8,top:'26px',zIndex:5,cursor:'pointer'}}
                          onMouseEnter={e=>{const r=e.currentTarget.getBoundingClientRect();setTooltip({x:r.left+18,y:r.top-8,row,isActual:true});}}
                          onMouseLeave={()=>setTooltip(null)}>
                          <MilestoneIcon color={ACTUAL_COLOR} size={22}/>
                          <div style={{position:'absolute',top:'100%',left:'50%',transform:'translateX(-50%)',fontSize:'8px',color:ACTUAL_COLOR,fontWeight:'700',whiteSpace:'nowrap',marginTop:'1px'}}>A</div>
                        </div>
                      )}
                    </>);
                  })()}

                  {/* MEETING — outlined calendar block with guaranteed full icon visibility */}
                  {isMeetingTask&&planned&&!planned.isDiamond&&(
                    <div
                      style={{
                        position:'absolute',
                        left: planned.left,
                        width: Math.max(DAY_W*1.5, planned.width),
                        top:'10px', height:'18px',
                        background:`${barC}18`,
                        borderRadius:'4px',
                        border:`1.5px solid ${barC}`,
                        zIndex:2, cursor:'pointer',
                        display:'flex', alignItems:'center',
                        gap:'3px', paddingLeft:'3px',
                        overflow:'visible',       // NEVER clip the icon
                        boxSizing:'border-box',
                      }}
                      onMouseEnter={e=>{const rect=e.currentTarget.getBoundingClientRect();setTooltip({x:rect.left,y:rect.top-8,row});}}
                      onMouseLeave={()=>setTooltip(null)}>
                      {/* Icon always visible — rendered in its own non-clipping wrapper */}
                      <div style={{flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',width:14,height:14}}>
                        <MeetingIcon color={barC} size={13}/>
                      </div>
                      {planned.width>48&&(
                        <span style={{fontSize:'9px',fontWeight:'600',color:barC,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:Math.max(0,planned.width-22),flex:'0 1 auto'}}>
                          {row.name}
                        </span>
                      )}
                    </div>
                  )}
                  {/* MEETING actual — thin stripe at same position as task actual bar */}
                  {isMeetingTask&&actual&&(showBar==='actual'||showBar==='both')&&(
                    <div style={{
                      position:'absolute',left:actual.left,
                      width:Math.max(DAY_W/2,actual.width),
                      top:'27px',height:'6px',
                      background:'#F97316',borderRadius:'3px',zIndex:3,
                      boxShadow:'0 1px 4px rgba(249,115,22,0.4)',cursor:'pointer',
                    }}
                      onMouseEnter={e=>{const r=e.currentTarget.getBoundingClientRect();setTooltip({x:r.left,y:r.top-8,row,isActual:true});}}
                      onMouseLeave={()=>setTooltip(null)}/>
                  )}

                  {/* BASELINE ghost bar (where it was originally planned) */}
                  {!isMilestoneTask&&!isMeetingTask&&baseBar&&!baseBar.isDiamond&&(
                    <div title="Baseline (original plan)" style={{
                      position:'absolute',left:baseBar.left,width:baseBar.width,
                      top:isPhase?'2px':isMS?'5px':'7px',
                      height:'4px',background:'#94A3B8',borderRadius:'2px',
                      opacity:0.7,zIndex:1,
                    }}/>
                  )}

                  {/* PLANNED BAR (regular task/milestone/phase) */}
                  {!isMilestoneTask&&!isMeetingTask&&planned&&!planned.isDiamond&&(
                    <div
                      style={{
                        position:'absolute',left:planned.left,width:planned.width,
                        top:isPhase?'6px':isMS?'9px':'11px',
                        height:isPhase?'24px':isMS?'20px':'16px',
                        background:criticalLive?'#DC2626':barC,borderRadius:isPhase?'6px':'4px',
                        opacity:showBar==='both'?0.7:0.88,zIndex:2,
                        overflow:'visible',boxShadow:criticalLive?'0 0 0 2px rgba(220,38,38,0.35)':criticalDone?'0 0 0 2px #DC2626':`0 1px 4px ${barC}40`,
                        cursor:'pointer',
                      }}
                      onMouseEnter={e=>{
                        const rect=e.currentTarget.getBoundingClientRect();
                        setTooltip({x:Math.min(rect.left,window.innerWidth-280),y:rect.top-8,row});
                      }}
                      onMouseLeave={()=>setTooltip(null)}>
                      {/* Progress fill */}
                      {row.progress>0&&(
                        <div style={{position:'absolute',left:0,top:0,bottom:0,width:`${Math.min(100,row.progress)}%`,background:'rgba(255,255,255,0.22)',borderRadius:'inherit'}}/>
                      )}
                      {/* Label — to the RIGHT of bar if bar is narrow, inside if wide */}
                      {planned.width>40&&(
                        <span style={{position:'absolute',left:'6px',top:'50%',transform:'translateY(-50%)',fontSize:'10px',fontWeight:'600',color:'#fff',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:planned.width-12,pointerEvents:'none'}}>
                          {isCritical?'▲ ':''}{row.name.length>18?row.name.slice(0,18)+'…':row.name}
                        </span>
                      )}
                    </div>
                  )}

                  {/* ACTUAL BAR — orange so it's clearly distinct from planned */}
                  {!isMilestoneTask&&!isMeetingTask&&actual&&(showBar==='actual'||showBar==='both')&&(
                    <div
                      style={{
                        position:'absolute',left:actual.left,width:actual.width,
                        top:isPhase?'30px':isMS?'29px':'27px',
                        height:'6px',
                        background:'#F97316',
                        borderRadius:'3px',zIndex:3,
                        boxShadow:'0 1px 6px rgba(249,115,22,0.5)',
                        cursor:'pointer',
                      }}
                      onMouseEnter={e=>{
                        const rect=e.currentTarget.getBoundingClientRect();
                        setTooltip({x:Math.min(rect.left,window.innerWidth-280),y:rect.top-8,row,isActual:true});
                      }}
                      onMouseLeave={()=>setTooltip(null)}/>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* TOOLTIP */}
      {tooltip&&(
        <GanttTooltip tooltip={tooltip}/>
      )}
    </div>
  );
}

// Tooltip component — fixed position
function GanttTooltip({tooltip}) {
  const {row,x,y,isActual} = tooltip;
  const STATUS_LABELS_MAP = {
    not_started:'Not Started',planning:'Planning',in_progress:'In Progress',
    escalated:'Escalated',at_risk:'At Risk',on_hold:'On Hold',
    completed:'Completed',cancelled:'Cancelled'
  };
  const sc = STATUS_COLORS[row.status]||'#8A93A6';
  const progress = Math.round(row.progress||0);

  return (
    <div style={{
      position:'fixed',
      left: Math.min(x, window.innerWidth-280),
      top: Math.max(8, y-130),
      width:'260px',
      background:'#1A1D23',
      color:'#fff',
      borderRadius:'10px',
      padding:'12px 14px',
      zIndex:9999,
      boxShadow:'0 8px 32px rgba(0,0,0,0.35)',
      pointerEvents:'none',
      border:'1px solid rgba(255,255,255,0.1)',
    }}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',gap:'8px',marginBottom:'10px',paddingBottom:'8px',borderBottom:'1px solid rgba(255,255,255,0.1)'}}>
        {row.task_type==='milestone'
          ? <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1L11 6L6 11L1 6Z" fill="#D97706"/></svg>
          : row.task_type==='meeting'
            ? <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" fill="#0891B2"/><path d="M6 3v3l2 1.5" stroke="white" strokeWidth="1.2" strokeLinecap="round"/></svg>
            : <div style={{width:10,height:10,borderRadius:'50%',background:STATUS_COLORS[row.status]||'#8A93A6',flexShrink:0,marginTop:'2px'}}/>}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:'13px',fontWeight:'700',lineHeight:1.3,wordBreak:'break-word'}}>{row.name}</div>
          <div style={{fontSize:'10px',color:'rgba(255,255,255,0.5)',marginTop:'2px',textTransform:'capitalize'}}>{row.type}{row.task_type&&row.type==='task'?` · ${row.task_type}`:''}</div>
        </div>
      </div>

      {/* Status */}
      {row.status&&(
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
          <span style={{fontSize:'10px',color:'rgba(255,255,255,0.5)'}}>Status</span>
          <span style={{fontSize:'11px',fontWeight:'600',padding:'1px 7px',borderRadius:'99px',background:`${sc}30`,color:sc}}>{STATUS_LABELS_MAP[row.status]||row.status}</span>
        </div>
      )}

      {/* Dates */}
      {isActual ? (
        <>
          <Row label="Actual Start" value={fmtDate(row.actual_start||row.start)} highlight/>
          <Row label="Actual End" value={fmtDate(row.actual_end)} highlight/>
        </>
      ) : (
        <>
          <Row label={row.task_type==='milestone'?'Due Date':'Planned Start'} value={fmtDate(row.task_type==='milestone'?row.end:row.start)}/>
          {row.task_type!=='milestone'&&<Row label="Planned End" value={fmtDate(row.end)}/>}
          {row.actual_start&&<Row label="Actual Start" value={fmtDate(row.actual_start)} highlight/>}
          {row.actual_end&&<Row label="Actual End" value={fmtDate(row.actual_end)} highlight/>}
        </>
      )}

      {/* Progress */}
      {(row.type==='task'||row.type==='subtask')&&(
        <div style={{marginTop:'8px',paddingTop:'8px',borderTop:'1px solid rgba(255,255,255,0.1)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'4px'}}>
            <span style={{fontSize:'10px',color:'rgba(255,255,255,0.5)'}}>Progress</span>
            <span style={{fontSize:'12px',fontWeight:'800',color:progress>=100?'#4ADE80':progress>=50?'#818CF8':'#FCD34D'}}>{progress}%</span>
          </div>
          <div style={{height:6,background:'rgba(255,255,255,0.1)',borderRadius:'99px',overflow:'hidden'}}>
            <div style={{height:'100%',width:`${progress}%`,background:progress>=100?'#4ADE80':progress>=50?'#818CF8':'#FCD34D',borderRadius:'99px'}}/>
          </div>
        </div>
      )}

      {/* Assignees */}
      {row.assignees?.length>0&&(
        <div style={{marginTop:'8px',fontSize:'10px',color:'rgba(255,255,255,0.5)'}}>
          👤 {row.assignees.map(a=>a.full_name).join(', ')}
        </div>
      )}

      {/* Time estimate */}
      {row.time_estimate>0&&(
        <div style={{marginTop:'4px',fontSize:'10px',color:'rgba(255,255,255,0.5)'}}>
          ⏱ {row.time_estimate}h estimated
        </div>
      )}
    </div>
  );
}

function Row({label,value,highlight}) {
  return (
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'4px'}}>
      <span style={{fontSize:'10px',color:'rgba(255,255,255,0.5)'}}>{label}</span>
      <span style={{fontSize:'11px',fontWeight:'600',color:highlight?'#86EFAC':'rgba(255,255,255,0.9)'}}>{value||'—'}</span>
    </div>
  );
}

function YearHeaderRow({ months, dayW }) {
  const yearGroups = [];
  months.forEach(function(m) {
    const yr = m.year;
    const last = yearGroups[yearGroups.length-1];
    if (last && last.year === yr) { last.days += m.days; }
    else { yearGroups.push({ year: yr, days: m.days }); }
  });
  return (
    <div style={{display:'flex',height:22,borderBottom:'1px solid var(--border)',background:'#EEF2FF'}}>
      {yearGroups.map(function(yg, i) {
        return (
          <div key={i} style={{width:yg.days*dayW,flexShrink:0,borderRight:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'800',color:'#5B5BD6',letterSpacing:'.5px'}}>
            {yg.year}
          </div>
        );
      })}
    </div>
  );
}

function LegendItem({color,label,height=8}) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:'5px',fontSize:'10px',color:'var(--muted)'}}>
      <div style={{width:20,height,borderRadius:'3px',background:color,flexShrink:0}}/>
      {label}
    </div>
  );
}
